<template>
  <!-- Q1 "Overall" from the Work Progress design: headline %, planned-vs-actual bar, four facts -->
  <v-card class="overall-card">
    <div class="d-flex justify-space-between align-baseline">
      <h2 class="card-title">{{ $t('taskOverview.overall') }}</h2>
      <span v-if="overview" class="text-caption text-medium-emphasis">{{ $t('taskSCurve.today') }}</span>
    </div>

    <v-skeleton-loader v-if="!overview && loading" type="article" />
    <div v-else-if="!overview" class="text-body-2 text-medium-emphasis">
      {{ $t(isTaskCrossFiltered ? 'taskOverview.noMatch' : 'taskSCurve.noData') }}
    </div>

    <template v-else>
      <div class="headline">
        <div class="stat">
          <span class="stat-label">{{ $t('taskSCurve.actual') }}</span>
          <span class="stat-big tk-ok">{{ pct(overview.actual) }}%</span>
        </div>
        <!-- PM lite has no planned dates, so no planned figure and no variance -->
        <template v-if="!isLite">
          <div class="stat">
            <span class="stat-label">{{ $t('taskSCurve.planned') }}</span>
            <span class="stat-big tk-info">{{ pct(overview.planned) }}%</span>
          </div>
          <div class="stat stat--variance">
            <span class="stat-label">{{ $t('taskSCurve.variance') }}</span>
            <span class="stat-mid" :class="overview.variance < 0 ? 'tk-err' : 'tk-ok'">
              {{ signed(overview.variance) }}%
            </span>
          </div>
        </template>
      </div>

      <div class="track">
        <div class="track-fill" :style="{ width: `${clamp(overview.actual)}%` }"></div>
        <div v-if="!isLite" class="track-marker" :style="{ left: `${clamp(overview.planned)}%` }"></div>
      </div>

      <div class="facts">
        <div v-for="fact in facts" :key="fact.label" class="fact">
          <span class="fact-label">{{ fact.label }}</span>
          <span class="fact-value" :class="fact.cls">{{ fact.value }}</span>
        </div>
      </div>
    </template>
  </v-card>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { formatDayMonthYear } from '@/utils/taskDates';
import { isTaskCrossFiltered } from '@/utils/taskSCurveFilters';

const props = defineProps({
  // Built by TasksPage.vue from taskStatusSql / scheduleSummarySql / the S-curve's today row
  overview: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const store = useStore();
const { t } = useI18n();
const locale = computed(() => store.getters.currentLocale);

const isLite = computed(() => props.overview?.mode === 'lite');

const pct = v => (v === null || v === undefined ? '–' : Number(v).toFixed(2));
const signed = v => `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}`;
const clamp = v => Math.min(Math.max(Number(v ?? 0), 0), 100);
const share = (n, total) => (total ? Math.round(n / total * 100) : 0);

const facts = computed(() => {
  const o = props.overview;
  if (!o) return [];
  const withShare = n => `${n.toLocaleString()} (${share(n, o.total)}%)`;

  if (isLite.value) {
    return [
      { label: t('taskStatus.totalTasks'), value: o.total.toLocaleString() },
      { label: t('taskStatus.notStarted'), value: withShare(o.notStarted) },
      { label: t('taskStatus.inProgress'), value: withShare(o.inProgress), cls: 'tk-info' },
      { label: t('taskStatus.completed'), value: withShare(o.completed), cls: 'tk-ok' },
    ];
  }

  // Days left turns amber inside the last two weeks and red (as "Overrun n") once the end has passed
  let daysLeft = { label: t('taskOverview.daysLeft'), value: '–' };
  if (o.daysLeft !== null) {
    daysLeft = o.daysLeft < 0
      ? { ...daysLeft, value: t('taskOverview.overrun', { n: -o.daysLeft }), cls: 'tk-err' }
      : { ...daysLeft, value: o.daysLeft.toLocaleString(), cls: o.daysLeft <= 14 ? 'tk-warn' : '' };
  }

  return [
    { label: t('taskStatus.totalTasks'), value: o.total.toLocaleString() },
    { label: t('taskStatus.completed'), value: withShare(o.completed), cls: 'tk-ok' },
    { label: t('taskOverview.plannedFinish'), value: formatDayMonthYear(o.plannedEndDate, locale.value) },
    daysLeft,
  ];
});
</script>

<style scoped>
.overall-card {
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px 18px;
}
.card-title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.headline {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
}
.stat {
  display: flex;
  flex-direction: column;
}
.stat--variance {
  padding-bottom: 4px;
}
.stat-label {
  font-size: 13px;
  opacity: 0.7;
}
.stat-big {
  font-size: 46px;
  line-height: 50px;
  font-variant-numeric: tabular-nums;
}
.stat-mid {
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.track {
  position: relative;
  height: 10px;
  border-radius: 5px;
  background: var(--tk-track);
}
.track-fill {
  height: 10px;
  border-radius: 5px;
  background: var(--tk-bar-ok);
  transition: width 0.4s ease;
}
.track-marker {
  position: absolute;
  top: -4px;
  width: 3px;
  height: 18px;
  margin-left: -1.5px;
  border-radius: 2px;
  background: var(--tk-info);
  transition: left 0.4s ease;
}
.facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px 16px;
  padding-top: 10px;
  border-top: 1px solid var(--tk-divider);
  margin-top: auto;
}
.fact {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
}
.fact-label {
  font-size: 12px;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fact-value {
  font-size: 22px;
  line-height: 28px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
@media (max-width: 480px) {
  .facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
